// useRequireApprovedCase — route guard hook for client case sub-pages
// that only make sense once the case has been approved by Coravu admin.
//
// The pages that use it (dashboard, candidates, positions, pipeline,
// shortlist) all assume there's actual case activity to show — students
// registered, submissions arriving, judges scoring, etc. For a draft or
// in-review case, those screens render mostly empty state and are
// confusing for the client. We bounce them to /edit instead.
//
// The sidebar already hides these nav items for pre-approval cases (see
// OverviewShell.tsx), but a user could still type the URL directly or
// land here from a stale link/bookmark. This hook is the second layer
// of defense.

import type { Href } from "expo-router";
import { router, useLocalSearchParams } from "expo-router";
import { useEffect, useState } from "react";

import { getClientCompetition } from "./clientCompetitions";

const APPROVED_STATUSES = [
  "approved",
  "contract_pending",
  "contract_signed",
  "payment_pending",
  "open",
  "judging",
  "closed",
  "final_invoice_pending",
  "final_invoice_paid",
];

/**
 * Hook: redirects to /edit if the case isn't approved-or-later.
 * Returns a `checking` flag — render a spinner / null while it's
 * resolving so we don't flash unauthorized content.
 */
export function useRequireApprovedCase(): { checking: boolean } {
  const { id } = useLocalSearchParams<{ id: string }>();
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    if (typeof id !== "string" || id.length === 0) {
      setChecking(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      const row = await getClientCompetition(id).catch(() => null);
      if (cancelled) return;
      const status = row?.status ?? "draft";
      if (!APPROVED_STATUSES.includes(status)) {
        router.replace(`/client/competition/${id}/edit` as Href);
        return;
      }
      setChecking(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  return { checking };
}
